import { useState, useEffect } from 'react'
import { Modal, TextField, BlockStack, InlineStack, Select } from '@shopify/polaris'

type Address = {
    firstName: string
    lastName: string
    company: string
    address1: string
    address2: string
    city: string
    zip: string
    countryCode: string
    phone: string
}

type Props = {
    open: boolean
    title: string
    address: Address
    onSave: (address: Address) => void
    onClose: () => void
}

const COUNTRIES = [
    { label: 'Argentina', value: 'AR' },
    { label: 'Australia', value: 'AU' },
    { label: 'Austria', value: 'AT' },
    { label: 'Belgium', value: 'BE' },
    { label: 'Brazil', value: 'BR' },
    { label: 'Bulgaria', value: 'BG' },
    { label: 'Canada', value: 'CA' },
    { label: 'Chile', value: 'CL' },
    { label: 'China', value: 'CN' },
    { label: 'Colombia', value: 'CO' },
    { label: 'Croatia', value: 'HR' },
    { label: 'Cyprus', value: 'CY' },
    { label: 'Czech Republic', value: 'CZ' },
    { label: 'Denmark', value: 'DK' },
    { label: 'Egypt', value: 'EG' },
    { label: 'Estonia', value: 'EE' },
    { label: 'Finland', value: 'FI' },
    { label: 'France', value: 'FR' },
    { label: 'Germany', value: 'DE' },
    { label: 'Greece', value: 'GR' },
    { label: 'Hong Kong', value: 'HK' },
    { label: 'Hungary', value: 'HU' },
    { label: 'Iceland', value: 'IS' },
    { label: 'India', value: 'IN' },
    { label: 'Indonesia', value: 'ID' },
    { label: 'Ireland', value: 'IE' },
    { label: 'Israel', value: 'IL' },
    { label: 'Italy', value: 'IT' },
    { label: 'Japan', value: 'JP' },
    { label: 'Latvia', value: 'LV' },
    { label: 'Liechtenstein', value: 'LI' },
    { label: 'Lithuania', value: 'LT' },
    { label: 'Luxembourg', value: 'LU' },
    { label: 'Malaysia', value: 'MY' },
    { label: 'Malta', value: 'MT' },
    { label: 'Mexico', value: 'MX' },
    { label: 'Morocco', value: 'MA' },
    { label: 'Netherlands', value: 'NL' },
    { label: 'New Zealand', value: 'NZ' },
    { label: 'Nigeria', value: 'NG' },
    { label: 'Norway', value: 'NO' },
    { label: 'Peru', value: 'PE' },
    { label: 'Philippines', value: 'PH' },
    { label: 'Poland', value: 'PL' },
    { label: 'Portugal', value: 'PT' },
    { label: 'Romania', value: 'RO' },
    { label: 'Saudi Arabia', value: 'SA' },
    { label: 'Singapore', value: 'SG' },
    { label: 'Slovakia', value: 'SK' },
    { label: 'Slovenia', value: 'SI' },
    { label: 'South Africa', value: 'ZA' },
    { label: 'South Korea', value: 'KR' },
    { label: 'Spain', value: 'ES' },
    { label: 'Sweden', value: 'SE' },
    { label: 'Switzerland', value: 'CH' },
    { label: 'Taiwan', value: 'TW' },
    { label: 'Thailand', value: 'TH' },
    { label: 'Turkey', value: 'TR' },
    { label: 'Ukraine', value: 'UA' },
    { label: 'United Arab Emirates', value: 'AE' },
    { label: 'United Kingdom', value: 'GB' },
    { label: 'United States', value: 'US' },
    { label: 'Vietnam', value: 'VN' },
]

export function EditAddressModal({ open, title, address: initial, onSave, onClose }: Props) {
    const [address, setAddress] = useState<Address>(initial)
    const [errors, setErrors] = useState<Record<string, string>>({})

    useEffect(() => {
        if (open) {
            setAddress(initial)
            setErrors({})
        }
    }, [open, initial])

    const update = (field: keyof Address) => (value: string) => {
        setAddress(prev => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors(prev => { const next = { ...prev }; delete next[field]; return next })
    }

    const handleSave = () => {
        const newErrors: Record<string, string> = {}
        if (!address.firstName.trim()) newErrors.firstName = 'Required'
        if (!address.lastName.trim()) newErrors.lastName = 'Required'
        if (!address.address1.trim()) newErrors.address1 = 'Required'
        if (!address.city.trim()) newErrors.city = 'Required'
        if (!address.zip.trim()) newErrors.zip = 'Required'
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }
        setErrors({})
        onSave(address)
        onClose()
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title}
            primaryAction={{ content: title.includes('billing') ? 'Done' : 'Save', onAction: handleSave }}
            secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
        >
            <Modal.Section>
                <BlockStack gap="400">
                    <Select
                        label="Country/region"
                        options={COUNTRIES}
                        value={address.countryCode}
                        onChange={update('countryCode')}
                        requiredIndicator
                    />
                    <InlineStack gap="400">
                        <div style={{ flex: 1 }}>
                            <TextField label="First name" value={address.firstName} onChange={update('firstName')} autoComplete="given-name" requiredIndicator error={errors.firstName} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <TextField label="Last name" value={address.lastName} onChange={update('lastName')} autoComplete="family-name" requiredIndicator error={errors.lastName} />
                        </div>
                    </InlineStack>
                    <TextField label="Company" value={address.company} onChange={update('company')} autoComplete="organization" />
                    <TextField label="Street and house number" value={address.address1} onChange={update('address1')} autoComplete="address-line1" requiredIndicator error={errors.address1} />
                    <TextField label="Additional address" value={address.address2} onChange={update('address2')} autoComplete="address-line2" />
                    <InlineStack gap="400">
                        <div style={{ flex: 1 }}>
                            <TextField label="Postal code" value={address.zip} onChange={update('zip')} autoComplete="postal-code" requiredIndicator error={errors.zip} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <TextField label="City" value={address.city} onChange={update('city')} autoComplete="address-level2" requiredIndicator error={errors.city} />
                        </div>
                    </InlineStack>
                    <TextField label="Phone" value={address.phone} onChange={update('phone')} autoComplete="tel" />
                </BlockStack>
            </Modal.Section>
        </Modal>
    )
}
